import React, { useContext, useEffect, useRef } from 'react';
import { FileUploaderGallery } from '../FileUploaderGallery/FileUploaderGallery';
import { FileGalleryRegistrationContext } from './FileGalleryRegistrationContext';
import type { GalleryResource } from '../FileUploaderGallery/FileUploaderGallery.types';
import type { FileManagerConfig } from './MasterCrud.types';

export interface MasterCrudFileManagerProps {
  /** Current record ID (undefined in CREATE mode until the record is saved) */
  entityId?: string | number;
  /** Entity name passed to the gallery */
  entityName: string;
  /** File management configuration (resources, callbacks) */
  config: FileManagerConfig;
}

/**
 * MasterCrudFileManager — embedded FileUploaderGallery inside a MasterCrud form tab.
 *
 * Renders the gallery without header/footer (embedded mode) and registers its
 * save callback via FileGalleryRegistrationContext so MasterCrud can trigger it
 * after the record is successfully saved.
 */
export const MasterCrudFileManager: React.FC<MasterCrudFileManagerProps> = ({
  entityId,
  entityName,
  config,
}) => {
  const { register } = useContext(FileGalleryRegistrationContext);
  const selectedResourcesRef = useRef<GalleryResource[]>([]);

  const handleResourceCheck = (uuid: string, checked: boolean, selectedResources: GalleryResource[]) => {
    selectedResourcesRef.current = selectedResources;
    config.onResourceCheck?.(uuid, checked, selectedResources);
  };

  const saveFileManagement = async (recordId: string | number): Promise<boolean> => {
    try {
      if (config.onAccept) {
        await config.onAccept(selectedResourcesRef.current, recordId);
      }
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    register(saveFileManagement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FileUploaderGallery
      title=""
      entity={config.entity || entityName}
      entityId={entityId ?? ''}
      mode="page"
      embedded
      resources={config.resources ?? []}
      allowSelection={config.allowSelection}
      allowDeletion={config.allowDeletion}
      showFileUploader={!!entityId && config.showFileUploader !== false}
      isLoading={config.isLoading}
      error={config.error}
      onUpload={config.onUpload && entityId ? (file) => config.onUpload!(file, entityId) : undefined}
      onDelete={config.onDelete && entityId ? (uuid) => config.onDelete!(uuid, entityId) : undefined}
      onResourceCheck={handleResourceCheck}
      acceptedExtensions={config.acceptedExtensions}
      maxFileSizeMB={config.maxFileSizeMB}
      maxTotalSizeMB={config.maxTotalSizeMB}
      multipleFiles={config.multipleFiles}
      showFileName={config.showFileName}
      acceptButtonText={config.acceptButtonText}
      className={config.className}
    />
  );
};
